import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InactiveStep from '../../InactiveStep';
import { useMultiStepFormContext } from '../../context/multistepContext';
import { useTranslationsContext } from '../../context/translationsContext';
import { FormStepProps } from '../../sendReceiptMultistep';
import { FormStepWrapper } from '../../sendReceiptMultistep.styles';
import Gift from './Gift';
import OpenShipment from './OpenShipment';
import SendAnInvoice from './SendAnInvoice';
import SendUsInformation from './SendUsInformation';
import ShipmentClearedByCustom from './ShipmentClearedByCustom';

export const INSTRUCTIONS_TO_STAFF_STEP_INDEX = 3;
export const SEPARATOR_CHAR = ';';

const INSTRUCTIONS_TO_STAFF_TO_COMPONENT_MAP: { [key: string]: React.FC<FormStepProps> } = {
	hasInvoice: SendAnInvoice,
	openUp: OpenShipment,
	alreadyCleared: ShipmentClearedByCustom,
	gift: Gift,
	other: SendUsInformation,
};

export default function InstructionToStaff({ values, touched, errors, setFieldValue, ...props }: FormStepProps) {
	const translations = useTranslationsContext();
	const { currentStepIndex, latestStepIndexVisited, goTo } = useMultiStepFormContext();
	const { t } = useTranslation('sendReceiptMultistep');
	const [selectedInstruction, setSelectedInstruction] = useState(() => values.instructionsToStaff);

	useEffect(() => {
		const resetFieldsBasedOnSelectedInstruction = () => {
			switch (values.instructionsToStaff) {
				case 'hasInvoice':
					setFieldValue('files', []);
					setFieldValue('openUp', '');
					setFieldValue('giftType', { label: 'Select', value: '' });
					setFieldValue('clearedTrackingNumber', '');
					setFieldValue('additionalInformation', '');
					break;
				case 'openUp':
					setFieldValue('files', []);
					setFieldValue('giftType', { label: 'Select', value: '' });
					setFieldValue('clearedTrackingNumber', '');
					setFieldValue('additionalInformation', '');
					break;
				case 'alreadyCleared':
					setFieldValue('files', []);
					setFieldValue('openUp', '');
					setFieldValue('giftType', { label: 'Select', value: '' });
					setFieldValue('additionalInformation', '');
					break;
				case 'gift':
					setFieldValue('files', []);
					setFieldValue('openUp', '');
					setFieldValue('clearedTrackingNumber', '');
					setFieldValue('additionalInformation', '');
					break;
				case 'other':
					setFieldValue('files', []);
					setFieldValue('openUp', '');
					setFieldValue('giftType', { label: 'Select', value: '' });
					setFieldValue('clearedTrackingNumber', '');
					setFieldValue('additionalInformation', '');
					break;
				default:
					break;
			}
		};

		if (values.instructionsToStaff !== selectedInstruction) {
			setSelectedInstruction(values.instructionsToStaff);
			resetFieldsBasedOnSelectedInstruction();
		}
	}, [values.instructionsToStaff, selectedInstruction, setFieldValue]);

	const Component = INSTRUCTIONS_TO_STAFF_TO_COMPONENT_MAP[values.instructionsToStaff];

	if (
		currentStepIndex !== INSTRUCTIONS_TO_STAFF_STEP_INDEX &&
		latestStepIndexVisited >= INSTRUCTIONS_TO_STAFF_STEP_INDEX
	)
		return (
			<InactiveStep
				heading={
					translations?.inactiveTitles?.type?.[values.instructionsToStaff] ||
					t(`inactiveTitles.type.${values.instructionsToStaff}`)
				}
				onClick={() => goTo(INSTRUCTIONS_TO_STAFF_STEP_INDEX)}
				touched={touched}
				errors={errors}
			/>
		);

	if (currentStepIndex === INSTRUCTIONS_TO_STAFF_STEP_INDEX)
		return (
			<FormStepWrapper>
				<Component
					values={values}
					touched={touched}
					errors={errors}
					setFieldValue={setFieldValue}
					{...props}></Component>
			</FormStepWrapper>
		);

	return null;
}
